/**
 * CompletedWorkPhoto — single photo card for John's real pool cage gallery.
 *
 * - Renders <picture> with a WebP source and a JPEG fallback so older
 *   browsers (or Safari + small holdouts) still see the image.
 * - Uses srcset for md (≤800w) and lg (≤1600w) variants so the browser
 *   picks an appropriately sized file.
 * - `loading="lazy"` everywhere except the first card on a page (eager,
 *   provided by `eager` prop) so layout doesn't jump on hero-adjacent
 *   sections. We always set width+height to prevent CLS.
 * - Per John's wording rules: never "before/after", never claim a specific
 *   city or service unless `confirmed_city` / `confirmed_service_type` is set.
 */
export default function CompletedWorkPhoto({ photo, eager = false, index = null, total = null, featured = false }) {
    const base = photo.base_path; // e.g. "/images/gallery/web/pool-cage-completed-work-01"
    const aspectClass = featured
        ? "aspect-[4/5] sm:aspect-[5/6] lg:aspect-auto lg:h-full"
        : photo.aspect_class || "aspect-[4/3]";
    const objPos = photo.object_position || "object-center";
    // Admin-uploaded photos arrive with a single hosted URL and no WebP/JPEG
    // responsive variants. Detect via the absence of base_path.
    const isHostedUrl = !base && !!photo.image_url;
    const counterLabel = (index !== null && total !== null)
        ? `${String(index + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`
        : null;

    return (
        <figure
            className={`a1-card a1-photo-card overflow-hidden p-0 flex flex-col${featured ? " a1-photo-card--feature" : ""}`}
            data-testid={`completed-work-photo-${photo.id}`}
        >
            <div
                className={`relative ${aspectClass} overflow-hidden`}
                style={{ background: "var(--color-bg-alt)" }}
            >
                {isHostedUrl ? (
                    <img
                        src={photo.image_url}
                        alt={photo.alt}
                        width={photo.intrinsic?.w}
                        height={photo.intrinsic?.h}
                        loading={eager ? "eager" : "lazy"}
                        decoding="async"
                        className={`a1-photo-img absolute inset-0 w-full h-full ${objPos}`}
                        style={{ objectFit: "cover" }}
                    />
                ) : (
                    <picture>
                        <source
                            type="image/webp"
                            srcSet={`${base}-md.webp 800w, ${base}-lg.webp 1600w`}
                            sizes={featured ? "(min-width: 1024px) 50vw, 100vw" : "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"}
                        />
                        <source
                            type="image/jpeg"
                            srcSet={`${base}-md.jpg 800w, ${base}-lg.jpg 1600w`}
                            sizes={featured ? "(min-width: 1024px) 50vw, 100vw" : "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"}
                        />
                        <img
                            src={`${base}-md.jpg`}
                            alt={photo.alt}
                            width={photo.intrinsic?.w}
                            height={photo.intrinsic?.h}
                            loading={eager ? "eager" : "lazy"}
                            decoding="async"
                            className={`a1-photo-img absolute inset-0 w-full h-full ${objPos}`}
                            style={{ objectFit: "cover" }}
                        />
                    </picture>
                )}
                {counterLabel ? (
                    <span className="a1-photo-counter" aria-hidden="true">{counterLabel}</span>
                ) : null}
            </div>
            <figcaption
                className="a1-photo-caption px-4 py-3 text-sm leading-relaxed border-t"
                style={{
                    color: "var(--color-text-muted)",
                    borderColor: "var(--color-line)",
                    background: "var(--color-bg-card)",
                }}
            >
                {photo.title || "Pool Cage & Screen Enclosure Project"}
            </figcaption>
        </figure>
    );
}
